import HttpRequest from 'httpRequest/HttpRequest'
import {
    CallerIdMessage,
    GetPrinterStatusPayload,
    PrinterCheckStatusPayload,
    PrinterSettings,
} from 'hooks/useHardwareAgent/Devices/types'
import { HardwareAgentContract } from '@models/HardwareAgentContract'

export interface IVersionModel {
    version: string
    informationalVersion: string
}

export interface UsbPrinter {
    deviceId: string
    deviceName: string
    manufacture: string
    productId: number
    vendorId: number
}

export interface WindowsPrinter {
    name: string
}

const KEY_HEADER = 'X-HWAgent-Key'
const DEFAULT_HW_AGENT_URI = 'https://localhost:5800'

export const constructHardwareAgentUri = (
    hardwareAgent:
        | Pick<HardwareAgentContract, 'schema' | 'host' | 'port'>
        | undefined,
) => {
    if (hardwareAgent) {
        return `${hardwareAgent.schema}${hardwareAgent.host}:${hardwareAgent.port}`
    } else {
        return DEFAULT_HW_AGENT_URI
    }
}

const createHardwareAgentService = (
    hwAgentUri: string,
    hwAgentKey?: string,
) => {
    const caller = new HttpRequest(hwAgentUri || DEFAULT_HW_AGENT_URI)
    caller.setHeader({ key: KEY_HEADER, value: hwAgentKey ?? '' })

    return {
        getVersion: () => caller.get<IVersionModel>('/system/version'),

        checkSystem: () => caller.get('/system'),

        getSecureConnect: () =>
            caller.get<{ enabled: boolean }>('/system/secure-connect'),

        setSecureConnect: (enabled: boolean) =>
            caller.post<boolean>('/system/secure-connect', enabled),

        print: (data: PrinterSettings, tag?: string) => {
            const url = tag ? `/printer/print?_tag=${tag}` : '/printer/print'
            return caller.post(url, data)
        },

        openDrawer: (data: PrinterSettings) =>
            caller.post('/printer/open-drawer', data),

        checkStatus: (payload: PrinterCheckStatusPayload) =>
            caller.post('/printer/check-status', payload),

        getPrinterStatus: (data: PrinterSettings) => {
            const payload: GetPrinterStatusPayload = { printer: data }
            return caller.post('/printer/get-status', payload)
        },

        listUsbPrinters: () =>
            caller.get<UsbPrinter[]>('/printer/list-usb-printers'),

        listWindowsPrinters: () =>
            caller.get<WindowsPrinter[]>('/printer/list-windows-printers'),

        getActiveCallerIds: () => caller.get<CallerIdMessage[]>('/callerid'),

        removeCallerId: (phoneNumber: string) =>
            caller.delete(`/callerid/${phoneNumber}`),

        getDownloadRootCaUri: () => `${hwAgentUri}/system/download-root-ca`,
    }
}

export type HardwareAgentService = ReturnType<typeof createHardwareAgentService>

export default createHardwareAgentService
